import React from 'react';
import { CareerSuggestion } from '../types';
import { Trophy, Briefcase, ArrowRight } from 'lucide-react';

interface CareerSuggestionsProps {
  suggestions: CareerSuggestion[];
}

export const CareerSuggestions: React.FC<CareerSuggestionsProps> = ({ suggestions }) => {
  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 flex items-center justify-center gap-3 mb-4">
          <Trophy className="w-8 h-8 text-yellow-500" />
          Your Career Matches
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Based on your responses, we've identified these career paths that align with your interests,
          values, and strengths. Each suggestion is tailored to your unique profile.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {suggestions.map((suggestion, index) => (
          <div
            key={index}
            className="bg-white p-6 rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Briefcase className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {suggestion.title}
                </h3>
                <p className="text-gray-600 mb-4">{suggestion.description}</p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Key Skills:</h4>
                    <div className="flex flex-wrap gap-2">
                      {suggestion.keySkills.map((skill, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Related Fields:</h4>
                    <div className="flex flex-wrap gap-2">
                      {suggestion.relatedFields.map((field, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full"
                        >
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <a
                    href={`https://www.onetonline.org/find/quick?s=${encodeURIComponent(suggestion.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    Learn more about this career
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-blue-50 p-6 rounded-lg mt-8">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          Next Steps
        </h3>
        <ul className="space-y-2 text-blue-800">
          <li className="flex items-start gap-2">
            <ArrowRight className="w-4 h-4 mt-1 flex-shrink-0" />
            Research these careers in depth using resources like O*NET and LinkedIn
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="w-4 h-4 mt-1 flex-shrink-0" />
            Connect with professionals in these fields for informational interviews
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="w-4 h-4 mt-1 flex-shrink-0" />
            Look for opportunities to gain experience through internships or projects
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="w-4 h-4 mt-1 flex-shrink-0" />
            Consider relevant courses or certifications to build required skills
          </li>
        </ul>
      </div>
    </div>
  );
};